import React from 'react';
import { usePOSStore } from '../../stores/pos.store';
import { useBaseCurrency, useActiveRate } from '../../db/hooks/useLookupData';
import { User, ShoppingCart, Plus, Wallet, AlertTriangle } from 'lucide-react';
import { Button } from '../common/Button';
import { formatLocal } from '../../lib/currency';

/* ────────────────────────────────────────────────────────────
 * POSCartSummarySidebar — Right-side persistent sidebar
 * Used during Tips and Payment steps (Fresha-style layout).
 * Shows: compact client card, cart items, financial breakdown,
 * and contextual primary action button.
 * ──────────────────────────────────────────────────────────── */

interface POSCartSummarySidebarProps {
  /** Primary action button config */
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: 'primary' | 'outline';
  };
  /** Secondary action (e.g. "Guardar pendiente") */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
}

export function POSCartSummarySidebar({ primaryAction, secondaryAction }: POSCartSummarySidebarProps) {
  const { cartItems, customer, discount, tip, setStep, paymentEntries, includePreviousDebt, surplusMode } = usePOSStore();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const { activeRate } = useActiveRate();

  const rateKey = baseCurrency.toLowerCase() as 'usd' | 'eur' | 'ves';
  const exchangeRateValue = rateKey === 'eur' ? (activeRate?.eur || 1) : rateKey === 'usd' ? (activeRate?.usd || 1) : 1;
  const toLocal = (val: number) => {
    if (rateKey === 'ves') return val;
    return Math.round((val * exchangeRateValue + Number.EPSILON) * 100) / 100;
  };

  const grossSubtotal = cartItems.reduce((acc, item) => acc + item.unitPrice * item.quantity, 0);
  const totalItemDiscounts = cartItems.reduce((acc, item) => acc + (item.discountAmount || 0) * item.quantity, 0);
  const totalGlobalDiscount = discount || 0;
  const totalDiscount = totalItemDiscounts + totalGlobalDiscount;
  const totalPaid = paymentEntries.reduce((acc, entry) => acc + entry.amount, 0);
  const netPayable = Math.max(0, grossSubtotal - totalDiscount) + tip;
  const pending = netPayable - totalPaid;
  const surplus = totalPaid > netPayable ? totalPaid - netPayable : 0;

  return (
    <>
      {/* Compact Client Card */}
      <div className="bg-surface-secondary backdrop-blur-md border border-border p-3.5 sm:p-4 rounded-2xl shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-brand-orange/10 rounded-full flex items-center justify-center border border-brand-orange/20 shrink-0">
            {customer ? (
              <span className="font-bold text-brand-orange text-sm">{customer.name.charAt(0).toUpperCase()}</span>
            ) : (
              <User className="w-5 h-5 text-text-tertiary" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-bold text-sm text-text-primary truncate">{customer?.name || 'Cliente Mostrador'}</h4>
            <p className="text-xs text-text-secondary truncate">{customer?.email || customer?.phone || 'Venta sin cliente'}</p>
          </div>
        </div>
        {customer && ((customer.credit_balance || 0) > 0 || (customer.debt_balance || 0) > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2 border-t border-border/50">
            {(customer.credit_balance || 0) > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                <Wallet className="w-3 h-3" />
                +{currencySymbol}{(customer.credit_balance || 0).toFixed(2)} A favor
              </span>
            )}
            {(customer.debt_balance || 0) > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                <AlertTriangle className="w-3 h-3" />
                -{currencySymbol}{(customer.debt_balance || 0).toFixed(2)} Deuda
              </span>
            )}
          </div>
        )}
      </div>

      {/* Cart Items Summary */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-3">
        {cartItems.map((item) => {
          const isPackage = item.type === 'package' || Boolean(item.package);
          const title = isPackage ? (item.package?.name || 'Combo') : (item.service?.concept || 'Servicio');
          const duration = !isPackage ? `${item.service?.duration_minutes || 30} min` : 'Paquete';
          const grossItemTotal = item.unitPrice * item.quantity;
          const itemDiscount = (item.discountAmount || 0) * item.quantity;
          const netItemTotal = Math.max(0, item.unitPrice - (item.discountAmount || 0)) * item.quantity;
          const hasDiscount = itemDiscount > 0;

          return (
            <div key={item.id} className="flex justify-between items-start border-l-2 border-brand-sky pl-3">
              <div className="min-w-0 flex-1 pr-2">
                <span className="font-bold text-sm text-text-primary block truncate">{title}</span>
                <div className="flex items-center gap-1.5 text-xs text-text-secondary flex-wrap">
                  <span>{duration}</span>
                  {hasDiscount && (
                    <span className="text-[10px] font-extrabold text-amber-500 bg-amber-500/10 px-1.5 py-0.2 rounded border border-amber-500/20">
                      Desc: -{currencySymbol}{itemDiscount.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="font-extrabold text-sm text-text-primary block">
                  {currencySymbol}{netItemTotal.toFixed(2)}
                </span>
                {hasDiscount && (
                  <span className="text-[11px] text-text-tertiary line-through block">
                    {currencySymbol}{grossItemTotal.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Add to cart shortcut */}
        <button
          onClick={() => setStep('cart')}
          className="flex items-center gap-2 text-xs font-bold text-text-secondary hover:text-brand-orange transition-colors pt-1 cursor-pointer"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          <span>Añadir al carrito</span>
        </button>
      </div>

      {/* Financial Breakdown */}
      <div className="border-t border-border pt-3 space-y-1.5 text-xs sm:text-sm">
        <div className="flex justify-between">
          <span className="text-text-secondary">Subtotal</span>
          <span className="font-bold text-text-primary">{currencySymbol}{grossSubtotal.toFixed(2)}</span>
        </div>

        {totalDiscount > 0 && (
          <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-extrabold">
            <span>Descuento</span>
            <span>-{currencySymbol}{totalDiscount.toFixed(2)}</span>
          </div>
        )}

        {includePreviousDebt && (customer?.debt_balance || 0) > 0 && (
          <div className="flex justify-between text-amber-600 dark:text-amber-400 font-bold">
            <span>Deuda anterior</span>
            <span>+{currencySymbol}{(customer?.debt_balance || 0).toFixed(2)}</span>
          </div>
        )}

        {tip > 0 && (
          <div className="flex justify-between text-brand-sky font-bold">
            <span>Propina</span>
            <span>+{currencySymbol}{tip.toFixed(2)}</span>
          </div>
        )}

        <div className="flex justify-between border-t border-border/60 pt-1.5 font-extrabold">
          <span className="text-text-primary">Monto Neto</span>
          <span className="text-text-primary">{currencySymbol}{netPayable.toFixed(2)}</span>
        </div>

        {totalPaid > 0 && (
          <div className="flex justify-between text-text-secondary">
            <span>Pagado</span>
            <span className="font-bold text-emerald-500">-{currencySymbol}{totalPaid.toFixed(2)}</span>
          </div>
        )}

        {surplus > 0 && (
          <div className="flex justify-between font-bold text-xs pt-0.5">
            <span className="text-text-secondary">{surplusMode === 'credit' ? 'Abonado a saldo:' : 'Vuelto:'}</span>
            <span className={surplusMode === 'credit' ? 'text-emerald-500' : 'text-brand-orange'}>+{currencySymbol}{surplus.toFixed(2)}</span>
          </div>
        )}

        <div className="flex justify-between items-start pt-1">
          <span className="font-bold text-text-primary">A pagar</span>
          <div className="text-right">
            <span className="text-base sm:text-lg font-black text-brand-orange block">{currencySymbol}{Math.max(0, pending).toFixed(2)}</span>
            {baseCurrency !== 'VES' && exchangeRateValue > 1 && (
              <span className="text-xs font-extrabold text-brand-sky block">{formatLocal(toLocal(Math.max(0, pending)))}</span>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2 pt-1">
        {primaryAction && (
          <Button
            variant={primaryAction.variant || 'primary'}
            fullWidth
            size="lg"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            className={primaryAction.variant === 'outline' ? '' : 'bg-brand-orange hover:bg-brand-orange/90 text-white border-none shadow-md shadow-brand-orange/20 font-black'}
            data-testid={`pos-sidebar-primary-${primaryAction.label.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {primaryAction.label}
          </Button>
        )}
        {secondaryAction && (
          <Button
            variant="outline"
            fullWidth
            size="lg"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            data-testid={`pos-sidebar-secondary-${secondaryAction.label.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {secondaryAction.label}
          </Button>
        )}
      </div>
    </>
  );
}
